// Botones de inicio de sesión / registro con Google y Apple.
//
// CONVIVE con el formulario email+contraseña: se muestra encima o debajo con un
// separador "o". Solo aparece si el login federado está configurado
// (`hasFederatedLogin()` → hay dominio de Hosted UI). Al pulsar, inicia el flujo
// OAuth (redirect al Hosted UI de Cognito); el retorno lo maneja
// `/auth/callback` (AuthCallbackView).
//
// `returnTo` permite volver a la ruta deseada tras autenticar (p. ej. continuar
// un checkout). Los logos usan SVG en línea (marca oficial, sin dependencias).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config, hasFederatedLogin } from '../config.js';
import { startFederatedLogin, type FederatedProvider } from './oauth.js';

/** Logo de Google (multicolor oficial). */
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

/** Logo de Apple (monocromo). */
function AppleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.04c-.03-2.52 2.06-3.73 2.15-3.79-1.17-1.71-2.99-1.95-3.64-1.97-1.55-.16-3.02.91-3.81.91-.78 0-1.99-.89-3.27-.86-1.68.02-3.23.98-4.1 2.48-1.75 3.03-.45 7.52 1.25 9.98.83 1.2 1.82 2.55 3.12 2.5 1.25-.05 1.72-.81 3.23-.81 1.51 0 1.93.81 3.25.78 1.34-.02 2.19-1.22 3.01-2.43.95-1.39 1.34-2.74 1.36-2.81-.03-.01-2.61-1-2.63-3.97zM14.53 4.47c.69-.83 1.15-1.99 1.02-3.15-.99.04-2.19.66-2.9 1.49-.64.74-1.2 1.92-1.05 3.05 1.1.09 2.23-.56 2.93-1.39z" />
    </svg>
  );
}

export function SocialLoginButtons({
  returnTo = '/',
  className = '',
}: {
  returnTo?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<FederatedProvider | null>(null);

  if (!hasFederatedLogin()) return null;

  async function go(provider: FederatedProvider) {
    setBusy(provider);
    try {
      await startFederatedLogin(provider, returnTo);
      // startFederatedLogin redirige el navegador; no vuelve aquí salvo error.
    } catch {
      setBusy(null);
    }
  }

  return (
    <div className={`flex flex-col gap-space-sm ${className}`}>
      {config.enableGoogleLogin && (
        <button
          type="button"
          onClick={() => void go('Google')}
          disabled={busy !== null}
          className="w-full min-h-[44px] py-2.5 px-space-md rounded-lg bg-white text-[#1f1f1f] font-body-md text-body-md font-medium border border-border-subtle hover:bg-white/90 transition-colors flex items-center justify-center gap-space-sm disabled:opacity-60"
        >
          <GoogleLogo />
          {t('social.continueWithGoogle')}
        </button>
      )}
      {config.enableAppleLogin && (
        <button
          type="button"
          onClick={() => void go('SignInWithApple')}
          disabled={busy !== null}
          className="w-full min-h-[44px] py-2.5 px-space-md rounded-lg bg-black text-white font-body-md text-body-md font-medium border border-black hover:bg-black/90 transition-colors flex items-center justify-center gap-space-sm disabled:opacity-60"
        >
          <AppleLogo />
          {t('social.continueWithApple')}
        </button>
      )}
    </div>
  );
}

/** Separador "o" entre el login social y el formulario email+contraseña. */
export function SocialDivider() {
  const { t } = useTranslation();
  if (!hasFederatedLogin()) return null;
  return (
    <div className="flex items-center gap-space-sm my-space-md" aria-hidden="true">
      <span className="flex-1 h-px bg-border-subtle" />
      <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wider">
        {t('social.or')}
      </span>
      <span className="flex-1 h-px bg-border-subtle" />
    </div>
  );
}
